import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@kognitos/lattice';

import type {
  Commonv1Value,
  V1AutomationConfig,
} from '@/generated/hooks/grimoire/models';

import { ManifestConfigTab } from '@/modules/automation/components/AutomationEditor/ManifestConfigTab';
import { DraftRailSection } from './DraftRailSection';

interface IRailTweaksSectionProps {
  automationConfig?: V1AutomationConfig;
  onSaveConfigValue?: (name: string, value: Commonv1Value) => Promise<void>;
}

/** The rail's Tweaks block: every config value the automation declares. */
export function RailTweaksSection({
  automationConfig,
  onSaveConfigValue,
}: IRailTweaksSectionProps) {
  const { t: tAutomation } = useTranslation('automation');
  const allConfigs = useMemo(
    () =>
      Object.entries(automationConfig ?? {}).map(([name, config]) => ({
        name,
        label: config.label || name,
        value: config.value,
      })),
    [automationConfig],
  );

  return (
    <DraftRailSection
      // Remounts so the block opens once configs arrive after the first render.
      key={allConfigs.length > 0 ? 'with-configs' : 'without-configs'}
      title={tAutomation('automation.draftRail.tweaks')}
      name="tweaks"
      count={allConfigs.length}
      defaultOpen={allConfigs.length > 0}
    >
      {allConfigs.length > 0 && onSaveConfigValue ? (
        <ManifestConfigTab configs={allConfigs} onSave={onSaveConfigValue} />
      ) : (
        <Text level="xSmall" color="muted" className="px-1 py-2">
          {tAutomation('automation.draftRail.noTweaks')}
        </Text>
      )}
    </DraftRailSection>
  );
}
